import React, { useState } from 'react';
import { useApp } from '../store';
import { Priority, UserRole } from '../types';
import { X, Calendar, Target, Flag, User, Paintbrush, Code, Clock } from 'lucide-react';

interface AddProjectModalProps {
  onClose: () => void;
}

export const AddProjectModal: React.FC<AddProjectModalProps> = ({ onClose }) => {
  const { addProject, clients, users } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    clientName: '',
    scope: '',
    priority: Priority.MEDIUM,
    overallDeadline: '',
    designDeadline: '',
    devDeadline: '',
    assignedDesignerId: '',
    assignedDevManagerId: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addProject(formData);
    onClose(); 
  };

  const designers = users.filter(u => u.role === UserRole.DESIGNER);
  const devManagers = users.filter(u => u.role === UserRole.DEV_MANAGER);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl p-8 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Create Project</h2>
            <p className="text-slate-500 text-sm font-medium">Define scope and phase milestones.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Project Title</label>
              <div className="relative">
                <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input 
                  required
                  type="text" 
                  placeholder="e.g., Brand Overhaul"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-bold text-slate-900 placeholder:font-medium"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Client Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input 
                  required
                  list="client-list"
                  type="text" 
                  placeholder="Select Client"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-semibold text-slate-900"
                  value={formData.clientName}
                  onChange={e => setFormData({...formData, clientName: e.target.value})}
                />
                <datalist id="client-list">
                  {clients.map(client => (
                    <option key={client} value={client} />
                  ))}
                </datalist>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Priority</label>
              <div className="relative">
                <Flag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select 
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-bold text-slate-900 appearance-none"
                  value={formData.priority}
                  onChange={e => setFormData({...formData, priority: e.target.value as Priority})}
                >
                  {Object.values(Priority).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-5 bg-slate-50 rounded-2xl border border-slate-100">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Clock className="w-3 h-3" /> Milestone Deadlines
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-1">Design Target</label>
                <input 
                  type="date" 
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700"
                  value={formData.designDeadline}
                  onChange={e => setFormData({...formData, designDeadline: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-[9px] font-black text-blue-500 uppercase tracking-widest mb-1">Dev Target</label>
                <input 
                  type="date" 
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700"
                  value={formData.devDeadline}
                  onChange={e => setFormData({...formData, devDeadline: e.target.value})}
                />
              </div>
              <div className="col-span-2 pt-2 border-t border-slate-200/50 mt-2">
                <label className="block text-[9px] font-black text-slate-900 uppercase tracking-widest mb-1">Final Project Delivery</label>
                <input 
                  required
                  type="date" 
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900"
                  value={formData.overallDeadline}
                  onChange={e => setFormData({...formData, overallDeadline: e.target.value})}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Assign Designer</label>
              <div className="relative">
                <Paintbrush className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select 
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-bold text-slate-900 appearance-none"
                  value={formData.assignedDesignerId}
                  onChange={e => setFormData({...formData, assignedDesignerId: e.target.value})}
                >
                  <option value="">Skip for now</option>
                  {designers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Assign Dev Lead</label>
              <div className="relative">
                <Code className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select 
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-bold text-slate-900 appearance-none"
                  value={formData.assignedDevManagerId}
                  onChange={e => setFormData({...formData, assignedDevManagerId: e.target.value})}
                >
                  <option value="">Skip for now</option>
                  {devManagers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <button type="submit" className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-100 hover:shadow-indigo-300 transition-all flex items-center justify-center gap-2 uppercase tracking-widest text-[10px]">
            Create Project
          </button>
        </form>
      </div>
    </div>
  );
};